import { useCallback, useEffect, useState } from 'preact/hooks'
import { useAccountLayoutContext } from './AccountProfileContext'
import { PageHeader } from './AccountSectionWrapper'
import { AccountWishlist } from './AccountWishlist'
import { Suspense } from './Suspense'
import { useToggle } from '../hooks/useToggle'
import { OmneoWishlistParser } from '../classes/omneoWishlistParser'
import { WishlistRelativeModal } from './WishlistRelativeModal'
import { useRefetch } from '../hooks/useRefetch'
import { getAllWishlists } from '../helpers/getAllWishlists'
import { accountTranslations } from '../helpers/translations'
const PRODUCT_IMAGE = 'https://cdn.shopify.com/s/files/1/0690/5538/1801/files/4-501874.jpg?v=1720590706&width=960'

export const RENDER_LIMIT_INDEX = 4

const WishlistRemainingItemCount = ({ remainingItems }) => {
  return (
    <div className={'absolute inset-0 m-auto h-full w-full'}>
      <div className={'paragraph-sm text-primary-white'}>+{remainingItems}</div>
    </div>
  )
}

export const handleItems = (items) => {
  const wishlist = items.slice(0, RENDER_LIMIT_INDEX)
  if (wishlist.length < RENDER_LIMIT_INDEX) {
    const placeHolder = Array.from(new Array(RENDER_LIMIT_INDEX - wishlist.length)).map((_) => ({
      meta: { img: undefined }
    }))
    return [...wishlist, ...placeHolder]
  }
  return wishlist
}

export const WishlistItemBlock = ({ item, totalItems, index, remainingItems }) => {
  return (
    <div className={'relative'}>
      {index + 1 === RENDER_LIMIT_INDEX && totalItems > RENDER_LIMIT_INDEX && (
        <WishlistRemainingItemCount remainingItems={remainingItems} />
      )}
      {!item?.meta?.img ? null : (
        <img className={'aspect-square w-full object-cover object-bottom'} src={item.meta.img} />
      )}
    </div>
  )
}

/**
 *
 * @param {{id: string, title: string, routeWishlist: (id: string) => void, icon?: string, wishlistItems: {id: string, image: string}[]}} props
 * @returns
 */
const WishListSection = (props) => {
  const handleRoute = () => {
    if (!props.id) return
    props.routeWishlist(props.id)
  }
  const { title = '', icon = undefined, profile, wishlistItems = [] } = props
  const items = wishlistItems || []
  const remainingItems = wishlistItems.length - RENDER_LIMIT_INDEX

  return (
    <div class={'flex flex-col gap-2'}>
      <div className={'flex w-full flex-row justify-between'}>
        <div className={'flex flex-row items-center gap-2'}>
          {profile?.connector?.avatar_url && (
            <img className={'h-8 w-8 rounded-full'} src={profile?.connector?.avatar_url} />
          )}
          <h3 className={'h3 text-primary-blue'}>{title}</h3>
        </div>
        <div className={'my-auto flex flex-row gap-4'}>
          <button disabled={!props.id} onClick={handleRoute} className={'font-bold underline underline-offset-2'}>
            {accountTranslations.wishlist.cta}
          </button>
        </div>
      </div>
      <div className={'grid grid-cols-4 gap-1'}>
        {items.length > 0 ? (
          items
            .sort((a, b) => {
              if (a?.meta?.img && !b?.meta?.img) {
                return -1 // a comes first
              }
              // Check if b has img defined and a does not
              if (!a?.meta?.img && b?.meta?.img) {
                return 1 // b comes first
              }
              return 0
            })
            .map((item, index) => {
              return (
                <WishlistItemBlock
                  index={index}
                  totalItems={items.length}
                  item={item}
                  remainingItems={remainingItems}
                />
              )
            })
        ) : (
          <div className={'aspect-square w-full bg-neutrals-base'}>
            <div className="flex flex-col items-center justify-center h-full">
              <p className="text-center">{accountTranslations.wishlist.no_items}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export const AccountWishlists = ({ props }) => {
  const [selectedWishlist, setSelectedWishlist] = useState()
  const { refetch, handleRefetch } = useRefetch()

  // null here means data has not loaded yet
  const [pageData, setPageData] = useState({
    wishlists: null,
    products: null
  })

  const { config, handPushChildState, omneoClient } = useAccountLayoutContext()

  const clearSelected = () => setSelectedWishlist(null)

  const routeWishlist = useCallback(
    (id) => {
      handPushChildState('wishlist', id)
    },
    [config]
  )

  useEffect(() => {
    if (!pageData.wishlists || !pageData.products) return // wait for data to load

    if (config.childPath !== '') {
      const wishlist = pageData.wishlists.find((o) => o.id === Number(config.childPath))
      if (!wishlist) {
        clearSelected()
        return
      }
      setSelectedWishlist(wishlist)
    } else {
      clearSelected()
    }
  }, [config.childPath, pageData])

  if (selectedWishlist) {
    return (
      <Suspense
        refetch={refetch}
        props={{
          asyncCallback: async () => {
            if (!refetch) return
            const wishlists = await getAllWishlists(omneoClient, false)
            const list = new OmneoWishlistParser(wishlists)
            const products = await list.mapOmneoListData()
            const wishlist = wishlists.find((w) => w.id === selectedWishlist.id)
            setPageData((c) => ({
              ...c,
              wishlists,
              products
            }))
            setSelectedWishlist(() => ({ ...wishlist }))
          }
        }}
      >
        <AccountWishlist
          handleRefetch={handleRefetch}
          wishlist={selectedWishlist}
          allProducts={pageData.products}
          omneoClient={omneoClient}
          clearSelected={clearSelected}
        />
      </Suspense>
    )
  }

  return (
    <div className={'flex flex-col gap-10'}>
      <PageHeader
        title={accountTranslations.wishlist.title}
        ctaChild={
          <WishlistRelativeModal
            title={accountTranslations.wishlist.create_wishlist}
            buttonTitle={accountTranslations.wishlist.create_wishlist}
            handleRefetch={handleRefetch}
          />
        }
      />
      <Suspense
        refetch={refetch}
        props={{
          skipLoad: false,
          asyncCallback: async () => {
            const wishlists = await getAllWishlists(omneoClient, false)
            const list = new OmneoWishlistParser(wishlists)
            const products = await list.mapOmneoListData()

            setPageData((c) => ({
              ...c,
              wishlists,
              products
            }))
          }
        }}
      >
        {pageData.wishlists?.map((wishlist) => {
          if (!wishlist) return null
          return (
            <WishListSection
              {...{
                id: wishlist.id,
                title: wishlist.name,
                routeWishlist,
                profile: wishlist.profile,
                wishlistItems: wishlist.items.filter((item) => item && item.product)
              }}
            />
          )
        })}
      </Suspense>
    </div>
  )
}
